import React, { useEffect, useMemo, useState, memo } from 'react';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { Trophy, Star, Clock, ArrowRight, Link as LinkIcon, TrendingUp, Lightbulb, Target, Flame, AlertTriangle, FileText, CalendarDays, History, Users, CheckCircle2 } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator, DjayBubble } from '../../../mascot';
import { MODULES, getModuleIcon } from '../../../content';
import { getWeeklyStats } from '../../../utils/activityHelpers';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { type LearnerAccount, type ParentAccount, type AccountsState } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
import { useModuleMastery } from '../hooks/useModuleMastery';
import { useWeeklyActivity } from '../hooks/useWeeklyActivity';
import { useChildDecay } from '../hooks/useChildDecay';
import { useLatestAssessment } from '../hooks/useLatestAssessment';
import { useLinkChild } from '../hooks/useLinkChild';
import { DataFreshness } from '../../shared/DataFreshness';
import { Skeleton } from '../../../components/shared/Skeleton';
import { getChildLastActivity } from '../shared/childActivity';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { getParentTipDismissals, isTipDismissed } from '../../../data/domains/preferences';
interface HomePageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
/**
 * Parent portal Home — 10-second dashboard.
 *
 * Stat-ownership (audit §16):
 * - Level + XP + current streak → ONLY here (compact hero chips)
 * - Weekly rhythm → compact strip here; canonical detail lives on Activity
 * - Decay alerts → top 2 here; full list on Tips
 * - Top tip → 1 here; full list on Tips
 * - Latest assessment → compact chip here; detail on Vakken (Toetsen-route in Wave 3)
 * - Overall mastery / module details → NOT shown here (canonical: Vakken)
 * - Days/Q/Accuracy this week → NOT shown here (canonical: Activity)
 *
 * Anti-pattern removed (Wave 2): quick-nav stat cards that re-rendered
 * "Gemiddelde beheersing" and "Oefendagen 3/7" — these duplicated the
 * canonical pages a click away. Replaced with a slim explore-row.
 */
export const HomePage = memo(function HomePage({
  activeAccount,
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner,
  linkParentToLearnerById,
  onNavigate
}: HomePageProps) {
  // All hooks MUST be called before any early return
  // W4-6: shared hook replaces inline useMemo (audit C-P-6)
  const {
    weeklyActivity
  } = useWeeklyActivity(childData);
  const weeklyStats = useMemo(() => getWeeklyStats(childData?.questionStates || {}), [childData?.questionStates]);
  const moduleMastery = useModuleMastery(selectedChild, getLearnerModuleMastery);
  // v1.6: decay-iteration extracted to useChildDecay hook (audit C-P-NEW-3).
  // Single source of truth for the family↔module mapping heuristic.
  const decay = useChildDecay(childData);
  // W4-2: respect parent-scoped tip dismissals on the Home preview slot.
  // Loaded async on mount + when the parent changes; empty map until ready.
  const [tipDismissals, setTipDismissals] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    void getParentTipDismissals(activeAccount.id).then(next => {
      if (!cancelled) setTipDismissals(next);
    });
    return () => {
      cancelled = true;
    };
  }, [activeAccount.id]);
  // Decay alerts — max 2, stale only. Same data as TipsPage but capped tighter.
  const decayAlerts = useMemo(() => {
    if (!selectedChild) return [];
    const alerts: Array<{
      module: (typeof MODULES)[number];
      daysSince: number;
      status: 'fading' | 'stale';
    }> = [];
    for (const module of MODULES) {
      const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
      if (mastery <= 0) continue;
      const info = decay.perModule[module.id];
      if (!info || info.worstStatus !== 'stale') continue;
      alerts.push({
        module,
        daysSince: info.daysSince,
        status: info.worstStatus
      });
    }
    return alerts.sort((a, b) => b.daysSince - a.daysSince).slice(0, 2);
  }, [selectedChild?.id, decay.perModule, getLearnerModuleMastery]);
  // Top tip — most actionable single signal
  const topTip = useMemo(() => {
    if (!selectedChild || !childData) return null;
    const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
    // Candidates share IDs with TipsPage so dismissals stay in sync.
    if (daysPracticed === 0 && !isTipDismissed(tipDismissals, 'rhythm-none')) {
      return {
        title: 'Start met een vast ritme',
        body: `Korte, dagelijkse sessies van 10 minuten werken beter dan één lange sessie.`,
        accent: 'amber' as const
      };
    }
    const weakest = moduleMastery.filter(m => m.mastery < 50).sort((a, b) => a.mastery - b.mastery)[0];
    if (weakest && !isTipDismissed(tipDismissals, `subject-weak-${weakest.module.id}`)) {
      return {
        title: `Extra aandacht voor ${weakest.module.title.toLowerCase()}`,
        body: `${selectedChild.displayName} oefent dit vak nog op ${weakest.mastery}%. Meester djay past het tempo automatisch aan.`,
        accent: 'rose' as const
      };
    }
    const currentStreak = childData.stats?.streakState?.currentStreak || 0;
    if (currentStreak >= 3 && !isTipDismissed(tipDismissals, 'streak-active')) {
      return {
        title: `${currentStreak} dagen op rij geoefend!`,
        body: `Een mooie streak. Stimuleer ${selectedChild.displayName} om de reeks vast te houden.`,
        accent: 'amber' as const
      };
    }
    return {
      title: 'Persoonlijke tips voor jullie',
      body: `Bekijk advies op maat over ritme, vakken en motivatie.`,
      accent: 'emerald' as const
    };
  }, [selectedChild?.id, childData, weeklyActivity, moduleMastery, tipDismissals]);
  // Latest assessment (compact chip — full detail on Vakken / future Toetsen route)
  // v1.6: extracted to useLatestAssessment hook (audit F-RUN-NEW-5).
  // v1.7: consume isLoading to render skeleton instead of flashing the empty
  // placeholder card on every page mount (audit P-2).
  const {
    report: latestAssessment,
    isLoading: isLoadingAssessment
  } = useLatestAssessment(selectedChild, selectedGroup);
  // Link child hook for empty state
  const {
    linkCode,
    setLinkCode,
    error,
    success,
    pendingLearner,
    warnings,
    hasGatingWarning,
    hasBlockingWarning,
    handleSubmit,
    handleConfirmLink,
    handleCancelLink
  } = useLinkChild({
    accounts: useAppStore.getState().accounts,
    parentId: activeAccount.id,
    linkedLearners,
    linkParentToLearnerById
  });
  // Empty state — no children linked
  if (linkedLearners.length === 0 || !selectedChild || !childData) {
    return <PageLayout title="Welkom!" description={`Fijn dat je er bent, ${activeAccount.displayName}`} data-id="element-8021">
        <div className="max-w-2xl mx-auto mt-12" data-id="element-8022">
          <div className="flex flex-col items-center mb-8" data-id="element-8023">
            <DjayNarrator size="lg" emotion="happy" animate data-id="element-8024" />
            <div className="mt-6 w-full max-w-sm" data-id="element-8025">
              <DjayBubble message="Hoi! Ik ben Djay. Laten we samen de voortgang van je kind volgen!" position="top" colorScheme="emerald" autoDismissMs={0} data-id="element-8026" />
            </div>
          </div>
          <Card className="p-8 border-emerald-100 shadow-sm text-center bg-white" data-id="element-8027">
            {success ? <div className="py-8 animate-in fade-in zoom-in duration-300" data-id="element-8028">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8029">
                  <CheckCircle2 className="w-8 h-8" data-id="element-8030" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-2" data-id="element-8031">
                  Gelukt!
                </h2>
                <p className="text-emerald-700 font-medium" data-id="element-8032">{success}</p>
              </div> : pendingLearner ? <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-8033">
                <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-8034">
                  Kind bevestigen
                </h2>
                <p className="text-slate-600 mb-6" data-id="element-8035">
                  Weet je zeker dat je{' '}
                  <span className="font-bold text-slate-900" data-id="element-8036">
                    {pendingLearner.displayName}
                  </span>{' '}
                  wilt koppelen aan jouw account?
                </p>

                {hasGatingWarning && warnings.length > 0 && <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left flex gap-3" data-id="element-8037">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8038" />
                    <div data-id="element-8039">
                      <h4 className="font-bold text-amber-900 text-sm" data-id="element-8040">
                        Let op
                      </h4>
                      <ul className="mt-1 space-y-1" data-id="element-8041">
                        {warnings.map((w, i) => <li key={i} className="text-sm text-amber-800" data-id="element-8042">
                            {w.message}
                          </li>)}
                      </ul>
                    </div>
                  </div>}

                <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-8043">
                  <Button variant="secondary" onClick={handleCancelLink} className="w-full sm:w-auto" data-id="element-8044">
                    Annuleren
                  </Button>
                  <Button onClick={handleConfirmLink} disabled={hasBlockingWarning} className={`w-full sm:w-auto text-white font-bold ${hasGatingWarning ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-id="element-8045">
                    {hasGatingWarning ? 'Toch koppelen' : 'Bevestigen'}
                  </Button>
                </div>
              </div> : <div className="animate-in fade-in duration-300" data-id="element-8046">
                <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-8047">
                  Koppel je kind
                </h2>
                <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-8048">
                  Vul de koppelcode van je kind in om de voortgang te kunnen
                  volgen. Je kunt deze code vinden in de instellingen van het
                  leerling-account.
                </p>
                <form onSubmit={handleSubmit} className="max-w-xs mx-auto" data-id="element-8049">
                  <div className="space-y-4" data-id="element-8050">
                    <Input value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} placeholder="Bijv. A1B2C3D4" className="text-center text-lg tracking-widest uppercase font-mono bg-slate-50" maxLength={8} data-id="element-8051" />
                    {error && <p className="text-sm text-rose-600 font-medium text-left" data-id="element-8052">
                        {error}
                      </p>}
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl" data-id="element-8053">
                      <LinkIcon className="w-4 h-4 mr-2" data-id="element-8054" />
                      Koppelen
                    </Button>
                  </div>
                </form>
              </div>}
          </Card>
        </div>
      </PageLayout>;
  }
  // Djay greeting selection
  const getDjayMessage = () => {
    if (weeklyStats.practiceDays >= 3) {
      return {
        emotion: 'proud' as const,
        message: `Wat een topweek! ${selectedChild.displayName} heeft al ${weeklyStats.practiceDays} dagen geoefend.`
      };
    }
    if (moduleMastery.length > 0 && moduleMastery[0].mastery > 70) {
      return {
        emotion: 'happy' as const,
        message: `${selectedChild.displayName} is super goed bezig met ${moduleMastery[0].module.title.toLowerCase()}!`
      };
    }
    if (weeklyStats.practiceDays === 0) {
      return {
        emotion: 'encouraging' as const,
        message: `Tijd voor een oefensessie? 15 minuutjes per dag maakt al een groot verschil!`
      };
    }
    return {
      emotion: 'happy' as const,
      message: `Hier is het overzicht van hoe het gaat met ${selectedChild.displayName}.`
    };
  };
  const djayMessage = getDjayMessage();
  const currentStreak = childData.stats?.streakState?.currentStreak || 0;
  const cito = latestAssessment ? levelCodeToCitoLevel(latestAssessment.overallEstimatedLevelCode, latestAssessment.selectedGroup) : null;
  return <PageLayout title="Overzicht" description="Hoe gaat het deze week?" actions={<DataFreshness updatedAt={getChildLastActivity(childData)} data-id="element-8056" />} data-id="element-8055">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-8057">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8058" />

        {/* Hero — solid emerald, compact Djay, three chips (Level / XP / Streak) */}
        <Card className="overflow-hidden border-none shadow-md bg-emerald-600 relative" data-id="element-8059">
          <div className="absolute inset-0 opacity-10" aria-hidden="true" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} data-id="element-8060" />
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-8061">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-inner flex-shrink-0" data-id="element-8062">
              <DjayNarrator size="md" emotion={djayMessage.emotion} data-id="element-8063" />
            </div>
            <div className="flex-1 text-center sm:text-left pt-1" data-id="element-8064">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2" data-id="element-8065">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-id="element-8066">
                  Hoe gaat het met {selectedChild.displayName}?
                </h1>
                <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap" data-id="element-8067">
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8068">
                    <Star className="w-3 h-3 fill-current" data-id="element-8069" /> Lvl{' '}
                    {childData.stats?.level || 1}
                  </span>
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8070">
                    <Trophy className="w-3 h-3" data-id="element-8071" /> {childData.stats?.xp || 0} XP
                  </span>
                  {currentStreak > 0 && <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8072">
                      <Flame className="w-3 h-3" data-id="element-8073" /> {currentStreak} dag
                      {currentStreak === 1 ? '' : 'en'}
                    </span>}
                </div>
              </div>
              <p className="text-emerald-50 text-lg font-medium max-w-2xl leading-relaxed" data-id="element-8074">
                {djayMessage.message}
              </p>
            </div>
          </div>
        </Card>

        {/* Compact weekly rhythm — minimal version; canonical detail on Activity */}
        <section data-id="element-8075">
          <div className="flex items-center justify-between mb-3" data-id="element-8076">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-8077">
              <Clock className="w-4 h-4 text-emerald-500" data-id="element-8078" />
              Deze week
            </h2>
            <button onClick={() => onNavigate?.('activiteit')} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-id="element-8079">
              Volledige kalender
              <ArrowRight className="w-3 h-3" data-id="element-8080" />
            </button>
          </div>
          <Card className="p-4 border-slate-200 shadow-sm bg-white" data-id="element-8081">
            <div className="flex justify-between items-center" data-id="element-8082">
              {weeklyActivity.map((day, i) => <div key={i} className="flex flex-col items-center gap-1.5 flex-1 min-w-0" data-id="element-8083">
                  <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-8084">
                    {day.label}
                  </div>
                  <div role="img" aria-label={day.practiced ? `${day.label}: ${day.questionCount} vragen geoefend${day.isToday ? ' (vandaag)' : ''}` : `${day.label}: nog niet geoefend${day.isToday ? ' (vandaag)' : ''}`} className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-[11px] font-black transition-all ${day.practiced ? 'bg-emerald-500 text-white shadow shadow-emerald-200' : day.isToday ? 'bg-white border-2 border-emerald-400 text-emerald-600' : 'bg-slate-50 border border-slate-100 text-slate-300'}`} data-id="element-8085">
                    {day.practiced ? day.questionCount : '·'}
                  </div>
                </div>)}
            </div>
          </Card>
        </section>

        {/* Decay alerts — only when there is something stale */}
        {decayAlerts.length > 0 && <section data-id="element-8086">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-8087">
              <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-8088" />
              Aandachtspunt
            </h2>
            <div className="space-y-2" data-id="element-8089">
              {decayAlerts.map(alert => {
            const IconComp = getModuleIcon(alert.module.icon);
            const weeksAgo = Math.floor(alert.daysSince / 7);
            const timeLabel = weeksAgo >= 4 ? 'meer dan een maand' : weeksAgo >= 2 ? `${weeksAgo} weken` : 'meer dan een week';
            return <Card key={alert.module.id} onClick={() => onNavigate?.('voortgang')} className="p-4 border border-rose-200 bg-rose-50/60 cursor-pointer hover:bg-rose-50 transition-colors flex items-center gap-3 group" data-id="element-8090">
                    <div className="w-10 h-10 rounded-xl bg-white text-rose-600 flex items-center justify-center flex-shrink-0 border border-rose-200" data-id="element-8091">
                      <IconComp className="w-5 h-5" data-id="element-8092" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-8093">
                      <div className="font-black text-sm text-rose-900 truncate" data-id="element-8094">
                        {alert.module.title} dreigt weg te zakken
                      </div>
                      <div className="text-xs font-medium text-rose-700/80 truncate" data-id="element-8095">
                        {timeLabel} niet geoefend — 10 minuten herhalen helpt.
                      </div>
                    </div>
                    <History className="w-4 h-4 text-rose-400 group-hover:translate-x-0.5 transition-transform" data-id="element-8096" />
                  </Card>;
          })}
            </div>
          </section>}

        {/* Tip + Latest Assessment — two compact slots side by side */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8097">
          {topTip && <Card onClick={() => onNavigate?.('tips')} className={`p-5 cursor-pointer hover:shadow-md transition-shadow border bg-white group ${topTip.accent === 'amber' ? 'border-amber-200' : topTip.accent === 'rose' ? 'border-rose-200' : 'border-emerald-200'}`} data-id="element-8098">
              <div className="flex items-start gap-3 mb-3" data-id="element-8099">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${topTip.accent === 'amber' ? 'bg-amber-50 text-amber-600' : topTip.accent === 'rose' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-8100">
                  {topTip.accent === 'rose' ? <Target className="w-5 h-5" data-id="element-8101" /> : topTip.accent === 'amber' ? <Clock className="w-5 h-5" data-id="element-8102" /> : <Lightbulb className="w-5 h-5" data-id="element-8103" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-8104">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-8105">
                    Tip van Djay
                  </div>
                  <h3 className="text-sm font-black text-slate-900 leading-snug" data-id="element-8106">
                    {topTip.title}
                  </h3>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed mb-3" data-id="element-8107">
                {topTip.body}
              </p>
              <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 group-hover:gap-1.5 transition-all" data-id="element-8108">
                <span data-id="element-8109">Alle tips</span>
                <ArrowRight className="w-3 h-3" data-id="element-8110" />
              </div>
            </Card>}

          {isLoadingAssessment ?
        // v1.7 (P-2): skeleton while the latest-assessment fetch is in
        // flight — prevents the "Nog geen toets gemaakt" flash on mount.
        <Card className="p-5 border border-slate-200 bg-white" aria-busy="true" aria-label="Laatste toets laden" data-id="element-8111">
              <div className="flex items-start gap-3 mb-3" data-id="element-8112">
                <Skeleton variant="rect" className="w-10 h-10" data-id="element-8113" />
                <div className="flex-1 min-w-0 space-y-2" data-id="element-8114">
                  <Skeleton className="w-20 h-3" data-id="element-8115" />
                  <Skeleton className="w-32 h-4" data-id="element-8116" />
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-8117">
                <div className="space-y-2" data-id="element-8118">
                  <Skeleton className="w-12 h-3" data-id="element-8119" />
                  <Skeleton className="w-10 h-5" data-id="element-8120" />
                </div>
                <Skeleton variant="rect" className="w-14 h-9" data-id="element-8121" />
              </div>
            </Card> : latestAssessment ? <Card onClick={() => onNavigate?.('toetsen')} className="p-5 cursor-pointer hover:shadow-md transition-shadow border border-slate-200 bg-white group" data-id="element-8122">
              <div className="flex items-start gap-3 mb-3" data-id="element-8123">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-indigo-50 text-indigo-600" data-id="element-8124">
                  <FileText className="w-5 h-5" data-id="element-8125" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-8126">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-8127">
                    Laatste toets
                  </div>
                  <h3 className="text-sm font-black text-slate-900 leading-snug" data-id="element-8128">
                    {new Date(latestAssessment.completedAt).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long'
                })}{' '}
                    · Groep {latestAssessment.selectedGroup}
                  </h3>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-8129">
                <div data-id="element-8130">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8131">
                    Niveau
                  </div>
                  <div className="text-lg font-black text-slate-800" data-id="element-8132">
                    {latestAssessment.overallEstimatedLevelCode}
                  </div>
                </div>
                {cito && <div className={`flex items-center justify-center px-3 py-2 rounded-xl border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8133">
                    <span className={`text-lg font-black ${cito.color}`} data-id="element-8134">
                      {cito.label}
                    </span>
                  </div>}
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" data-id="element-8135" />
              </div>
            </Card> :
        // Placeholder when there's no assessment yet
        <Card className="p-5 border border-dashed border-slate-200 bg-slate-50/40 flex items-center gap-3" data-id="element-8136">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-400" data-id="element-8137">
                <FileText className="w-5 h-5" data-id="element-8138" />
              </div>
              <div data-id="element-8139">
                <div className="text-xs font-bold text-slate-500" data-id="element-8140">
                  Nog geen toets gemaakt
                </div>
                <div className="text-[11px] text-slate-400 leading-snug" data-id="element-8141">
                  Zodra {selectedChild.displayName} een toets afrondt verschijnt
                  hier het niveau.
                </div>
              </div>
            </Card>}
        </section>

        {/* Slim explore-row — replaces the duplicate stat-cards. No numbers here:
            this is pure navigation, not a stat-restate. */}
        <section data-id="element-8142">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8143">
            Verder kijken
          </h2>
          <div className={`grid gap-3 ${linkedLearners.length >= 2 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`} data-id="element-8144">
            <button onClick={() => onNavigate?.('voortgang')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 focus-visible:outline-none focus-visible:border-emerald-400 focus-visible:bg-emerald-50/60 focus-visible:ring-2 focus-visible:ring-emerald-300 transition-all text-left group" data-id="element-8145">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-8146">
                <TrendingUp className="w-4 h-4" data-id="element-8147" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8148">
                <div className="text-sm font-black text-slate-900" data-id="element-8149">Vakken</div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8150">
                  Per-vak beheersing
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" data-id="element-8151" />
            </button>

            <button onClick={() => onNavigate?.('activiteit')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 focus-visible:outline-none focus-visible:border-blue-400 focus-visible:bg-blue-50/60 focus-visible:ring-2 focus-visible:ring-blue-300 transition-all text-left group" data-id="element-8152">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-8153">
                <CalendarDays className="w-4 h-4" data-id="element-8154" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8155">
                <div className="text-sm font-black text-slate-900" data-id="element-8156">
                  Activiteit
                </div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8157">
                  Kalender & reeksen
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" data-id="element-8158" />
            </button>

            <button onClick={() => onNavigate?.('tips')} className={`flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/40 focus-visible:outline-none focus-visible:border-amber-400 focus-visible:bg-amber-50/60 focus-visible:ring-2 focus-visible:ring-amber-300 transition-all text-left group ${linkedLearners.length >= 2 ? '' : 'col-span-2 sm:col-span-1'}`} data-id="element-8159">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-8160">
                <Lightbulb className="w-4 h-4" data-id="element-8161" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8162">
                <div className="text-sm font-black text-slate-900" data-id="element-8163">Tips</div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8164">
                  Alle adviezen
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500 transition-colors" data-id="element-8165" />
            </button>

            {/* W4-1 — Vergelijken: only surfaces for multi-child accounts (Mara persona) */}
            {linkedLearners.length >= 2 && <button onClick={() => onNavigate?.('compare')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 focus-visible:outline-none focus-visible:border-violet-400 focus-visible:bg-violet-50/60 focus-visible:ring-2 focus-visible:ring-violet-300 transition-all text-left group" data-id="element-8166">
                <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0" data-id="element-8167">
                  <Users className="w-4 h-4" data-id="element-8168" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-8169">
                  <div className="text-sm font-black text-slate-900" data-id="element-8170">
                    Vergelijken
                  </div>
                  <div className="text-xs text-slate-500 truncate" data-id="element-8171">
                    Kinderen naast elkaar
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-violet-500 transition-colors" data-id="element-8172" />
              </button>}
          </div>
        </section>
      </div>
    </PageLayout>;
});